// The Runs page (docs/cockpit/spec.md): every Run, past and present, that the person may see, newest first.
// Filtered by what it waits on (live, waiting for you, finished) and searched by the server; a row opens the
// Run's panel.
import { useState } from "react";
import type { Api, RunRow, RunShow } from "../api.js";
import { useWords, type Words } from "../i18n/words.js";
import { ListSearch, ListView, Segments, useList } from "../lists/List.js";

const shows = (w: Words): readonly (readonly [RunShow, string])[] => [
  ["all", w.runsAll],
  ["live", w.runsLive],
  ["waiting", w.runsWaiting],
  ["finished", w.runsFinished],
];

function Stage({ row }: { row: RunRow }) {
  const w = useWords();
  if (row.status === "completed") return <span className="tag tag-good">{w.runsStageFinished}</span>;
  const where = row.waitingOn ?? row.step ?? w.runsRunning;
  return (
    <>
      <span className={row.needsYou ? "tag tag-need" : "tag tag-plain"}>{where}</span>
      {row.late ? <span className="tag tag-bad">{w.runsLate}</span> : null}
    </>
  );
}

export function RunsList({
  api,
  initialQuery = "",
  onOpenRun,
}: {
  api: Pick<Api, "runs">;
  initialQuery?: string;
  onOpenRun: (run: string) => void;
}) {
  const w = useWords();
  const [show, setShow] = useState<RunShow>("all");
  const [q, setQ] = useState(initialQuery);
  const { loaded, setPage } = useList(api.runs, { show, q });
  return (
    <>
      <div className="hello">
        <h2>{w.runsTitle}</h2>
        <p className="muted">{w.runsIntro}</p>
      </div>
      <div className="toolbar">
        <Segments label={w.runsShow} options={shows(w)} value={show} onChange={setShow} />
        <ListSearch label={w.runsSearch} initial={initialQuery} onSearch={setQ} />
      </div>
      <ListView loaded={loaded} what={w.runsWhat} empty={q ? w.runsNoMatch(q) : w.runsNone} onPage={setPage}>
        {(rows) => (
          <table className="tbl">
            <thead>
              <tr>
                <th scope="col">{w.runsRun}</th>
                <th scope="col">{w.runsValueStream}</th>
                <th scope="col">{w.runsStage}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.run}>
                  <th scope="row">
                    <button type="button" className="linkish" onClick={() => onOpenRun(row.run)}>
                      {row.title}
                    </button>
                  </th>
                  <td>{row.valueStream ?? "—"}</td>
                  <td>
                    <Stage row={row} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ListView>
    </>
  );
}
